import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './HomePage.css';

const HomePage = () => {
  // Load initial state from localStorage
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('vfootball-filter') || 'All';
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('vfootball-language') || 'EN';
  });
  const [currentDate] = useState(new Date());
  const [userTimezone] = useState(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [showMatchInfo, setShowMatchInfo] = useState(null);
  const [starredMatches, setStarredMatches] = useState(() => {
    const saved = localStorage.getItem('vfootball-starred');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const matchesPerPage = 15;
  const navigate = useNavigate();


  const handleWatchMatch = (matchId) => {
    console.log('Navigating to match:', matchId);
    navigate(`/match/${matchId}`);
  };

  const handleShowMatchInfo = (matchId) => {
    setShowMatchInfo(showMatchInfo === matchId ? null : matchId);
  };

  const handleStarMatch = (matchId) => {
    setStarredMatches(prev => {
      const newStarred = new Set(prev);
      if (newStarred.has(matchId)) {
        newStarred.delete(matchId);
      } else {
        newStarred.add(matchId);
      }
      
      // Save to localStorage
      localStorage.setItem('vfootball-starred', JSON.stringify(Array.from(newStarred)));
      
      return newStarred;
    });
    // Reset to first page when starring/unstarring since starred matches show first
    setCurrentPage(1);
  };

  // Handle tab change with pagination reset
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs

    // Save to localStorage
    localStorage.setItem('vfootball-filter', tab);
  };

  // Handle language change
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    setCurrentPage(1); // Reset to first page when changing language

    // Save to localStorage
    localStorage.setItem('vfootball-language', language);

    // Refetch matches with new language
    fetchMatches();
  };

  // Fetch matches from API
  const fetchMatches = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await apiService.getLeagueMatches({ language: selectedLanguage, all_languages: 'true' });

      if (data.success && data.leagues) {
        // Convert the new API structure to the expected format
        const groupedMatches = data.leagues.map(league => ({
          league: {
            name: league.name,
            logo: league.logo,
            flag: league.flag,
            country: league.country
          },
          leagueFlag: league.flag,
          matches: league.matches
        }));
        setMatches(groupedMatches);
      } else {
        setError(data.error || 'Failed to fetch matches');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching matches:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group matches by league
  const groupMatchesByLeague = (matchList) => {
    const grouped = {};
    
    matchList.forEach(match => {
      const leagueKey = match.league?.name || 'Unknown League';
      if (!grouped[leagueKey]) {
        grouped[leagueKey] = {
          league: match.league,
          leagueFlag: match.league?.flag,
          matches: []
        };
      }
      grouped[leagueKey].matches.push(match);
    });
    
    return Object.values(grouped);
  };

  // Load matches on component mount + auto-refresh every 20s (silent)
  useEffect(() => {
    fetchMatches();
    const intervalId = setInterval(() => fetchMatches(true), 20000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (date) => {
    const options = { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      timeZone: userTimezone
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'TBD';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: userTimezone
    });
  };

  const getTimezoneAbbr = () => {
    const date = new Date();
    const timezone = date.toLocaleDateString('en-US', {
      day: '2-digit',
      timeZoneName: 'short'
    }).split(', ')[1];
    return timezone;
  };

  // Helper function to normalize match status
  const normalizeStatus = (status) => {
    if (!status) return 'scheduled';
    
    const statusStr = typeof status === 'string' ? status : status.short || status.long || '';
    const upperStatus = statusStr.toUpperCase();
    
    // Map backend statuses to frontend expectations
    if (upperStatus === 'NS' || upperStatus === 'TBD' || upperStatus === 'SCH') {
      return 'scheduled';
    }
    if (upperStatus === 'FT' || upperStatus === 'AET' || upperStatus === 'PEN') {
      return 'finished';
    }
    if (upperStatus === 'LIVE' || upperStatus === '1H' || upperStatus === '2H' || 
        upperStatus === 'HT' || upperStatus === 'ET' || upperStatus === 'P') {
      return 'live';
    }
    if (upperStatus === 'PST' || upperStatus === 'CANC' || upperStatus === 'ABD' || upperStatus === 'SUSP') {
      return 'postponed';
    }
    
    return 'scheduled';
  };

  // Show loading state
  if (loading) {
    return (
      <div className="livescores-app">
        <main className="main-content">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Loading matches...</p>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="livescores-app">
        <main className="main-content">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>Error: {error}</p>
            <button onClick={fetchMatches} style={{ marginTop: '20px' }}>Retry</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="livescores-app">
      <main className="main-content">
        <div className="promo-banner" style={{ display: 'none' }}>
          <div className="promo-content">
            <div className="promo-text">
              <h2 className="promo-title">Custom Name Jerseys</h2>
              <p className="promo-subtitle">Get your favorite team's jersey with your name and number! Premium quality, authentic designs.</p>
              <button className="sign-in-btn">Shop Now</button>
            </div>
            <div className="promo-image">
              {/* Add your image here */}
            </div>
          </div>
        </div>

        <div className="matches-section">
          <div className="filter-section">
            <div className="filter-tabs">
              {['All', 'Live', 'Soon', 'Finished'].map(tab => (
                <button
                  key={tab}
                  className={`filter-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>


            <div className="date-info">
              <svg width="16" height="16" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {formatDate(currentDate)}
              <span className="timezone-display">({getTimezoneAbbr()})</span>
            </div>
          </div>

          <div className="matches-container">
            {(() => {
              // Get all matches and add starred status + normalized status
              const allMatches = matches.flatMap(leagueGroup => 
                leagueGroup.matches.map(match => ({ 
                  ...match, 
                  isStarred: starredMatches.has(match.id),
                  normalizedStatus: normalizeStatus(match.status),
                  isLive: normalizeStatus(match.status) === 'live'
                }))
              );

              // Filter matches based on active tab
              const filteredMatches = allMatches.filter(match => {
                // Apply tab filters only - API handles date filtering
                if (activeTab === 'All') return true;
                if (activeTab === 'Live') return match.normalizedStatus === 'live';
                if (activeTab === 'Soon') return match.normalizedStatus === 'scheduled';
                if (activeTab === 'Finished') return match.normalizedStatus === 'finished';
                return true;
              }).sort((a, b) => {
                // First sort by starred
                if (a.isStarred && !b.isStarred) return -1;
                if (!a.isStarred && b.isStarred) return 1;
                // Then sort by time
                return new Date(a.fixture?.date || a.date || a.time) - new Date(b.fixture?.date || b.date || b.time);
              });

              // Calculate pagination
              const totalMatches = filteredMatches.length;
              const totalPages = Math.ceil(totalMatches / matchesPerPage);
              const startIndex = (currentPage - 1) * matchesPerPage;
              const endIndex = startIndex + matchesPerPage;
              const paginatedMatches = filteredMatches.slice(startIndex, endIndex);

              return paginatedMatches.map(match => (
              <div key={match.id} className="match-card">
                <button
                  className={`star-btn ${match.isStarred ? 'starred' : ''}`}
                  onClick={() => handleStarMatch(match.id)}
                >
                  <svg viewBox="0 0 24 24">
                    <path 
                      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" 
                      fill={match.isStarred ? "currentColor" : "none"} 
                      stroke="currentColor" 
                      strokeWidth="2"
                    />
                  </svg>
                </button>
                
                <span className="time-minute">
                  {match.normalizedStatus === 'finished' ? 'FT' :
                   match.normalizedStatus === 'scheduled' ? 'SC' :
                   match.isLive ? `${match.minute || match.elapsed || 'LIVE'}${match.minute ? "'" : ''}` :
                   'SC'}
                </span>
                
                <img
                  src={match.teams?.home?.logo || match.homeTeamLogo || match.homeLogo}
                  alt={match.teams?.home?.name || match.homeTeam}
                  className="team-logo home-logo"
                  onError={(e) => { e.target.src = '/default-team-logo.png'; }}
                />

                <span className="team-name home-name">{match.teams?.home?.name || match.homeTeam}</span>
                
                <span className="score">
                  {match.normalizedStatus === 'scheduled'
                    ? formatTime(match.fixture?.date || match.date || match.time)
                    : `${match.score?.home ?? match.homeScore ?? 0} : ${match.score?.away ?? match.awayScore ?? 0}`
                  }
                </span>
                
                <span className="team-name away-name">{match.teams?.away?.name || match.awayTeam}</span>

                <img
                  src={match.teams?.away?.logo || match.awayTeamLogo || match.awayLogo}
                  alt={match.teams?.away?.name || match.awayTeam}
                  className="team-logo away-logo"
                  onError={(e) => { e.target.src = '/default-team-logo.png'; }}
                />
                
                <div className="info-btn-container">
                  <button className="info-btn">
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
                      <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  <div className="match-info-popup">
                    <div className="match-info-content">
                      <div className="info-item">
                        <span className="info-label">League:</span>
                        <span className="info-value">{match.league?.name || 'Unknown League'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Venue:</span>
                        <span className="info-value">{match.venue || 'TBD'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Status:</span>
                        <span className="info-value">{match.status}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  className="tv-btn"
                  onClick={() => handleWatchMatch(match.id)}
                >
                  <svg viewBox="0 0 24 24">
                    <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                    <line x1="7" y1="21" x2="17" y2="21" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>
              ));
            })()}
          </div>

          {/* Pagination Component */}
          {(() => {
            // Calculate pagination info
            const allMatches = matches.flatMap(leagueGroup => 
              leagueGroup.matches.map(match => ({ 
                ...match, 
                isStarred: starredMatches.has(match.id),
                normalizedStatus: normalizeStatus(match.status),
                isLive: normalizeStatus(match.status) === 'live'
              }))
            );
            
            const filteredMatches = allMatches.filter(match => {
              // Apply tab filters only - API handles date filtering
              if (activeTab === 'All') return true;
              if (activeTab === 'Live') return match.normalizedStatus === 'live';
              if (activeTab === 'Soon') return match.normalizedStatus === 'scheduled';
              if (activeTab === 'Finished') return match.normalizedStatus === 'finished';
              return true;
            });

            const totalMatches = filteredMatches.length;
            const totalPages = Math.ceil(totalMatches / matchesPerPage);
            const startIndex = (currentPage - 1) * matchesPerPage;
            const showingCount = Math.min(matchesPerPage, totalMatches - startIndex);

            if (totalPages <= 1) return null; // Don't show pagination if only 1 page

            const handlePageChange = (newPage) => {
              setCurrentPage(newPage);
              // Scroll to top of matches
              document.querySelector('.matches-section')?.scrollIntoView({ behavior: 'smooth' });
            };

            return (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {startIndex + 1}-{startIndex + showingCount} of {totalMatches} matches
                </div>
                <div className="pagination-controls">
                  <button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </button>
                  <button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show pages around current page
                      return page === 1 || page === totalPages || 
                             (page >= currentPage - 2 && page <= currentPage + 2);
                    })
                    .map((page, index, array) => {
                      // Add ellipsis if there's a gap
                      const elements = [];
                      if (index > 0 && page > array[index - 1] + 1) {
                        elements.push(
                          <span key={`ellipsis-${page}`} className="pagination-ellipsis">...</span>
                        );
                      }
                      elements.push(
                        <button
                          key={page}
                          className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                      return elements;
                    }).flat()}
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                  <button 
                    className="pagination-btn"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </main>
    </div>
  );
};

export default HomePage;